/**
 * File Storage API Types
 * Types for file upload and management
 */
import type { CommonPaginationDto } from "./common";
import type { DocumentListItemDto } from "./documents";

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Status values for files - represents processing pipeline stages
 */
export enum FileStatusEnum {
    Pending = "Pending",
    Uploaded = "Uploaded",
    OCR = "OCR",
    Classification = "Classification",
    Processing = "Processing",
    Completed = "Completed",
    Failed = "Failed",
    Deleted = "Deleted",
}

// =============================================================================
// DTOs
// =============================================================================

/**
 * File storage information - returned from GET /files and GET /files/{id}
 */
export interface FileStorageResponseDto {
    id: number;
    fileName: string;
    contentType: string;
    /** Canonical server-derived format: "Pdf" | "Xls" | "Xlsx" | "Doc" | "Docx" | "Unknown" (rows predating file-type validation) */
    fileType: string;
    fileSizeBytes: number;
    pageCount: number;
    checksum?: string;
    uploadedAt: string;
    status: FileStatusEnum;
    blobPath: string;
    documents: DocumentListItemDto[];
    /** Organization that owns this file (optional until processed) */
    organizationId?: number;
    /** Name of the organization */
    organizationName?: string;
    /** Optional error code from the latest exception related to this file */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, error is transient and will be retried automatically. errorCode/errorMessage are always null in this state. */
    hasRetryWarning?: boolean;
}

/**
 * File content with metadata - returned from GET /files/{id}/content
 * Contains the actual file bytes as base64 encoded string
 */
export interface FileContentResponseDto {
    id: number;
    fileName: string;
    contentType: string;
    /** Canonical server-derived format: "Pdf" | "Xls" | "Xlsx" | "Doc" | "Docx" | "Unknown" — drives Workspace viewer routing */
    fileType: string;
    fileSizeBytes: number;
    content: string; // Base64 encoded byte array
    blobPath: string;
    checksum?: string;
}

/**
 * Request body for updating file properties
 */
export interface FileStorageUpdateDto {
    id: number;
    fileName?: string;
    pageCount?: number;
}

/**
 * Request body for updating file status
 */
export interface FileStatusUpdateDto {
    fileId: number;
    status: FileStatusEnum;
}

/**
 * Lightweight file status for polling with documents
 * Returned from GET /files/{id}/status and POST /files/status/batch/get
 */
export interface FileStatusDto {
    id: number;
    status: FileStatusEnum;
    updatedAt: string;
    /** Optional error code from the latest exception related to this file */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, error is transient and will be retried automatically. errorCode/errorMessage are always null in this state. */
    hasRetryWarning?: boolean;
    /** Documents extracted from this file (when available) */
    documents: DocumentListItemDto[];
}

/**
 * Request body for batch file status retrieval
 */
export interface GetFileStatusBatchRequestDto {
    fileIds: number[];
}

/**
 * Response for batch file status retrieval
 */
export interface GetFileStatusBatchResponseDto {
    statuses: FileStatusDto[];
    total: number;
    notFoundIds: number[];
}

// =============================================================================
// REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Request parameters for listing files
 */
export interface FileListParams {
    pageSize?: number;
    skip?: number;
}

/**
 * Response for file list endpoint
 */
export interface FileListResponseDto {
    data: FileStorageResponseDto[];
    pagination: CommonPaginationDto;
}

/**
 * File raw text response with metadata - returned from GET /files/{id}/rawtext
 * Contains the extracted/OCR raw text for a file
 */
export interface FileRawTextResponseDto {
    id: number;
    fileName: string;
    contentType: string;
    fileSizeBytes: number;
    rawText?: string | null;
    blobPath: string;
    checksum?: string;
}

/**
 * Response for checking upload availability
 * Returned from GET /files/can-upload
 */
export interface CanUploadResponseDto {
    /** Whether the client can upload files */
    canUpload: boolean;
    /** Remaining page quota for current month (null if unlimited) */
    remainingQuota?: number | null;
    /** Current page usage for current month (null if unlimited) */
    currentUsage?: number | null;
    /** Total page quota limit (null if unlimited) */
    quotaLimit?: number | null;
    /** Whether a quota limit is configured */
    hasQuota: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a file status is a final state (stop polling)
 */
export function isFileFinalStatus(status: FileStatusEnum): boolean {
    return [FileStatusEnum.Completed, FileStatusEnum.Failed, FileStatusEnum.Deleted].includes(status);
}

/**
 * Check if a file status indicates active processing
 */
export function isFileProcessingStatus(status: FileStatusEnum): boolean {
    return [FileStatusEnum.Pending, FileStatusEnum.Uploaded, FileStatusEnum.OCR, FileStatusEnum.Classification, FileStatusEnum.Processing].includes(status);
}

/**
 * Check if a file status is an error state
 */
export function isFileErrorStatus(status: FileStatusEnum): boolean {
    return status === FileStatusEnum.Failed;
}

/**
 * Check if a file status DTO indicates an error state
 * Error state is: status is Failed OR errorCode is populated
 */
export function isFileStatusError(statusDto: FileStatusDto): boolean {
    return statusDto.status === FileStatusEnum.Failed || !!statusDto.errorCode;
}

/**
 * Check if file has documents ready for polling
 */
export function hasDocumentsForPolling(file: FileStorageResponseDto): boolean {
    return file.documents && file.documents.length > 0;
}
